/**
 * Connectors API — system-wide registry (Milestone 1.5b).
 *
 * Reads open to any auth user; writes require system_role=founder.
 *
 * Mirrors apps/chaos-gateway/src/connectors.ts.
 */
import type {
  Connector,
  CreateConnectorInput,
  UpdateConnectorInput,
} from '@chaos/types/registries';

import { apiJson } from './client';
import { normalizeOrgPublications } from './org-publication-shape';

interface ConnectorsListResponse {
  connectors: Connector[];
}
interface ConnectorResponse {
  connector: Connector;
}

// ── Cross-org publication matrix (0079) ────────────────────────────────
// MIRRORS skillsApi (0078) EXACTLY. A connector is PUBLISHED (made available)
// into an org via a connector_org_publications row. // connector is available in EVERY org of the account and the per-org cells
// render as inherited. `orgIds` is the explicit per-org set. FOUNDER-RESOLVED
// semantics: pure checkbox — All-orgs → everywhere; specific org rows → those;
// no rows → available nowhere. Publication is the FIRST gate; a role in the org
// must ALSO be granted the connector (role_connector_grants) to actually use it.
/**
 * WHERE an item is published, and which of those marks the "All organizations"
 * select-all button put there (migration 0168).
 *
 *   orgIds       — every organization the item is published into. THE list.
 *   viaAllOrgIds — the subset select-all placed (⊆ orgIds). The origin is
 *                  remembered because two rules depend on it: a new solo
 *                  Workspace inherits these marks, and the first other person
 *                  added to a Workspace switches them off. Marks placed by hand
 *                  are never touched by either.
 *
 * There is no `allOrgs` boolean any more. "Available everywhere" used to be a
 * separate STATUS row (org_id IS NULL) which made it impossible to exclude a
 * single organization — and which, because no read gate carried an account
 * filter, actually meant "every organization that will ever exist, on any
 * account". It is now simply the state where every organization is ticked,
 * which the UI reads off `orgIds` and therefore cannot disagree about.
 */
export interface ConnectorPublications {
  orgIds: string[];
  viaAllOrgIds: string[];
}

// One column of the cross-org matrix — an org of the caller's account.
export interface ConnectorMatrixOrg {
  id: string;
  displayName: string;
  logoUrl: string | null;
}

// One row of the matrix (GET /api/connectors/all): the catalog Connector plus
// its publication set. Connectors have NO owner org (global catalog).
export interface ConnectorMatrixRow extends Connector {
  publications: ConnectorPublications;
}

export interface ConnectorMatrix {
  connectors: ConnectorMatrixRow[];
  orgs: ConnectorMatrixOrg[];
}

interface ConnectorPublicationsResponse {
  publications: ConnectorPublications;
}

interface ListConnectorsParams {
  category?: string;
  enabled?: boolean;
  [k: string]: string | number | boolean | undefined;
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export const connectorsApi = {
  list(params: ListConnectorsParams = {}): Promise<Connector[]> {
    return apiJson<ConnectorsListResponse>(`/connectors${qs(params)}`).then(
      (r) => r.connectors,
    );
  },
  // 0079: account-wide matrix — every connector in the global catalog, each with
  // its cross-org publication set, plus the account's orgs as the matrix columns.
  // Account-owner only (403 otherwise). Mirrors skillsApi.listAll().
  //
  // THE PUBLICATION SETS ARE NORMALISED AT THE DOOR (see
  // ./org-publication-shape). This is the only route that puts a publication
  // set into the react-query cache, and the component it feeds —
  // <CrossOrgMatrix> — is SHARED with templates, permissions and roles, so one
  // absent field here is four Settings screens, not one. Take it out and a
  // payload whose `orgIds` is not an array takes the section down through
  // `cellState`, which React evaluates while building the element tree.
  listAll(): Promise<ConnectorMatrix> {
    return apiJson<ConnectorMatrix>(`/connectors/all`).then((m) => ({
      ...m,
      connectors: m.connectors.map((c) => ({
        ...c,
        publications: normalizeOrgPublications(c.publications),
      })),
    }));
  },
  get(connectorId: string): Promise<Connector> {
    return apiJson<ConnectorResponse>(`/connectors/${connectorId}`).then((r) => r.connector);
  },
  create(input: CreateConnectorInput): Promise<Connector> {
    return apiJson<ConnectorResponse>('/connectors', { method: 'POST', json: input }).then(
      (r) => r.connector,
    );
  },
  update(connectorId: string, input: UpdateConnectorInput): Promise<Connector> {
    return apiJson<ConnectorResponse>(`/connectors/${connectorId}`, {
      method: 'PATCH',
      json: input,
    }).then((r) => r.connector);
  },
  disable(connectorId: string): Promise<Connector> {
    return apiJson<ConnectorResponse>(`/connectors/${connectorId}`, {
      method: 'DELETE',
    }).then((r) => r.connector);
  },
  // ── 0079: cross-org publication ──────────────────────────────────────
  // Reconcile the FULL publication set in one call. Mirrors skillsApi.setPublications().
  setPublications(
    connectorId: string,
    pub: ConnectorPublications,
  ): Promise<ConnectorPublications> {
    return apiJson<ConnectorPublicationsResponse>(`/connectors/${connectorId}/publications`, {
      method: 'PUT',
      json: { orgIds: pub.orgIds },
    }).then((r) => r.publications);
  },
  // Toggle ONE matrix cell ON. orgId=null = the all-organizations sentinel.
  // Tick ONE organization. 0168: `orgId: null` used to mean the sentinel and is
  // now a 400 — selectAll() is the route for that.
  addPublication(connectorId: string, orgId: string): Promise<ConnectorPublications> {
    return apiJson<ConnectorPublicationsResponse>(`/connectors/${connectorId}/publications`, {
      method: 'POST',
      json: { orgId },
    }).then((r) => r.publications);
  },

  // THE SELECT-ALL. Puts a separate mark on every organization of the account;
  // any one of them can then be unticked on its own.
  selectAllOrgs(connectorId: string): Promise<ConnectorPublications> {
    return apiJson<ConnectorPublicationsResponse>(`/connectors/${connectorId}/publications/all`, {
      method: 'POST',
    }).then((r) => r.publications);
  },

  // The same box, unticked. IT REMOVES ONLY THE MARKS IT MADE — hand-placed
  // marks survive, which is why the header box can end up half-selected.
  clearAllOrgs(connectorId: string): Promise<ConnectorPublications> {
    return apiJson<ConnectorPublicationsResponse>(`/connectors/${connectorId}/publications/all`, {
      method: 'DELETE',
    }).then((r) => r.publications);
  },
  // Toggle ONE matrix cell OFF. orgId=null = remove the all-orgs sentinel.
  // Untick ONE organization. Works the same whether the mark was hand-placed or
  // put there by select-all — "after that, any one of them can be unticked" is
  // the whole point of 0168.
  removePublication(connectorId: string, orgId: string): Promise<ConnectorPublications> {
    return apiJson<ConnectorPublicationsResponse>(
      `/connectors/${connectorId}/publications?orgId=${encodeURIComponent(orgId)}`,
      { method: 'DELETE' },
    ).then((r) => r.publications);
  },
  // Convenience: flip one cell to the desired state. Mirrors togglePublication().
  // Flip one matrix cell. `orgId === null` is the HEADER box (select-all), which
  // since 0168 is a real bulk write rather than a status flag.
  togglePublication(
    connectorId: string,
    orgId: string | null,
    on: boolean,
  ): Promise<ConnectorPublications> {
    if (orgId === null) {
      return on ? this.selectAllOrgs(connectorId) : this.clearAllOrgs(connectorId);
    }
    return on ? this.addPublication(connectorId, orgId) : this.removePublication(connectorId, orgId);
  },
};

export const connectorsKeys = {
  all: ['connectors'] as const,
  list: (params: ListConnectorsParams = {}) => [...connectorsKeys.all, 'list', params] as const,
  detail: (connectorId: string) => [...connectorsKeys.all, 'detail', connectorId] as const,
  // 0079: account-wide publication matrix (GET /connectors/all).
  matrix: () => [...connectorsKeys.all, 'matrix'] as const,
};
